import { Suspense, lazy } from 'react'
import { useParams } from 'react-router-dom'
import { useCostDrivers } from '../api/hooks'
import { StaticDriverEditor } from './StaticDriverEditor'

/*
  The SQL editor is loaded on demand.

  It carries Monaco, which is an order of magnitude larger than the rest of the
  application put together. Most people never open a SQL driver, and the landing page is
  the QC dashboard — none of them should pay for the editor to get there.
*/
const SqlDriverEditor = lazy(() =>
  import('./SqlDriverEditor').then((m) => ({ default: m.SqlDriverEditor })),
)

/**
 * Picks the editor that matches the driver's type.
 *
 * One route rather than two, because a driver's type is a property of the driver, not
 * something the person following a link is expected to know. A bookmarked
 * `/drivers/17` opens the right editor whichever kind 17 turns out to be.
 */
export function DriverEditor() {
  const { id } = useParams<{ id: string }>()
  const { data: drivers = [], isLoading } = useCostDrivers()
  const driver = drivers.find((d) => d.costDriverId === Number(id))

  if (isLoading || !driver) {
    return (
      <p className="p-4 text-[12px]" style={{ color: 'var(--color-muted)' }}>
        {isLoading ? 'Loading driver…' : `Cost driver ${id} was not found.`}
      </p>
    )
  }

  if (driver.driverType !== 'Sql') {
    return <StaticDriverEditor />
  }

  return (
    <Suspense
      fallback={
        <p className="p-4 text-[12px]" style={{ color: 'var(--color-muted)' }}>
          Loading editor…
        </p>
      }
    >
      <SqlDriverEditor />
    </Suspense>
  )
}
